// Activity page (Multi-user Stage B.5 + filters/charts).
//
// Top-down: trend strip → breakdowns (drill-through filters) → filter
// bar → cursor-paginated list. The URL is the source of truth for both
// filters and the chart window — the page is shareable end-to-end.
//
// Purpose: spot data-quality and ops issues fast (a sub-agent whose
// grounding fell off, a day with a latency spike, a route_class that
// suddenly costs 5× more) so they can be investigated separately.

import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow, parseISO } from "date-fns";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { ActivityBreakdowns } from "@/components/activity/ActivityBreakdowns";
import { ActivityChartStrip } from "@/components/activity/ActivityChartStrip";
import { ActivityFilters as ActivityFiltersBar } from "@/components/activity/ActivityFilters";
import { useActivityFilters } from "@/hooks/useActivityFilters";
import { fetchActivity, fetchActivityStats, type ActivityTraceRow } from "@/services/api/me";
import { cn } from "@/utils/cn";

function relativeTime(iso: string | null): string {
  if (!iso) return "";
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return "";
  }
}

function GroundingBadge({ score }: { score: number | null }) {
  if (score == null) {
    return <span className="text-xs italic text-warmgray-400">—</span>;
  }
  const tone =
    score >= 0.85 ? "text-espresso-700" : score >= 0.6 ? "text-warmgray-500" : "text-oxblood-500";
  return <span className={cn("font-mono text-xs", tone)}>{score.toFixed(2)}</span>;
}

function CostCell({ usd }: { usd: string | null }) {
  if (!usd) return <span className="text-xs italic text-warmgray-400">—</span>;
  return <span className="font-mono text-xs text-espresso-700">${usd}</span>;
}

function LatencyCell({ ms }: { ms: number | null }) {
  if (ms == null) return <span className="text-xs italic text-warmgray-400">—</span>;
  const tone = ms >= 30_000 ? "text-oxblood-500" : ms >= 10_000 ? "text-warmgray-500" : "text-espresso-700";
  const display = ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
  return <span className={cn("font-mono text-xs", tone)}>{display}</span>;
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status || status === "succeeded") return null;
  return (
    <span className="rounded-sm border border-oxblood-500 bg-oxblood-500/10 px-1 py-0.5 font-mono text-[0.6rem] uppercase tracking-wider text-oxblood-500">
      {status}
    </span>
  );
}

function SubAgentsCell({ agents }: { agents: string[] }) {
  if (!agents.length) return <span className="text-xs italic text-warmgray-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {agents.map((a) => (
        <span
          key={a}
          className="rounded-sm border border-ruleline bg-parchment-50 px-1.5 py-0.5 text-[0.65rem] font-mono text-warmgray-500"
        >
          {a}
        </span>
      ))}
    </div>
  );
}

function ActivityRow({ trace }: { trace: ActivityTraceRow }) {
  const linkTo = trace.session_id
    ? `/history/${encodeURIComponent(trace.session_id)}?session=${encodeURIComponent(trace.session_id)}`
    : null;

  const inner = (
    <>
      <div className="flex items-center gap-2 text-[0.65rem] uppercase tracking-wider text-warmgray-400">
        <span>{relativeTime(trace.created_at)}</span>
        {trace.route_class ? (
          <>
            <span>·</span>
            <span className="text-oxblood-500">{trace.route_class}</span>
          </>
        ) : null}
        {trace.refine_used ? (
          <>
            <span>·</span>
            <span className="text-warmgray-500">refine</span>
          </>
        ) : null}
        <StatusBadge status={trace.status} />
      </div>
      <div className="mt-1 truncate font-serif text-sm text-espresso-700">
        {trace.query_preview || (
          <span className="italic text-warmgray-400">(empty query)</span>
        )}
      </div>
      {trace.answer_summary_preview ? (
        <div className="mt-1 line-clamp-2 text-xs text-warmgray-500">
          {trace.answer_summary_preview}
        </div>
      ) : null}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        <div className="flex items-center gap-1.5">
          <span className="text-[0.65rem] uppercase tracking-wider text-warmgray-400">
            grounding
          </span>
          <GroundingBadge score={trace.grounding_score} />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[0.65rem] uppercase tracking-wider text-warmgray-400">cost</span>
          <CostCell usd={trace.cost_usd} />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[0.65rem] uppercase tracking-wider text-warmgray-400">
            retrieval
          </span>
          <LatencyCell ms={trace.latency_ms} />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[0.65rem] uppercase tracking-wider text-warmgray-400">tools</span>
          <span className="font-mono text-xs text-espresso-700">{trace.tool_count}</span>
        </div>
        <SubAgentsCell agents={trace.sub_agents_used} />
      </div>
    </>
  );

  return (
    <li className="border-b border-ruleline last:border-b-0">
      {linkTo ? (
        <Link
          to={linkTo}
          className="block px-4 py-3 transition-colors hover:bg-parchment-50"
        >
          {inner}
        </Link>
      ) : (
        <div className="px-4 py-3">{inner}</div>
      )}
    </li>
  );
}

export function Activity() {
  const filterState = useActivityFilters();
  const { filters, window } = filterState;

  // Cursor stack lets the user step back through pages. Reset to the
  // first page whenever any filter changes — older cursors target a
  // different result set.
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const currentCursor = cursorStack[cursorStack.length - 1] ?? null;

  // ``filters`` is a fresh object every render (built from URLSearchParams),
  // so we can't use it directly as a query key — its identity would force
  // a refetch even when the values didn't change. Hash to a stable JSON
  // string for keying and reset state.
  const filterKey = JSON.stringify(filters);
  useEffect(() => {
    setCursorStack([null]);
  }, [filterKey]);

  const listQuery = useQuery({
    queryKey: ["me", "activity", filterKey, currentCursor],
    queryFn: () => fetchActivity(currentCursor, filters),
  });
  const traces = listQuery.data?.traces ?? [];
  const nextCursor = listQuery.data?.next_cursor ?? null;
  const canGoBack = cursorStack.length > 1;

  const statsQuery = useQuery({
    queryKey: ["me", "activity", "stats", window],
    queryFn: () => fetchActivityStats(window),
    staleTime: 30_000,
  });

  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-10">
      <header className="mb-8">
        <p className="smallcaps text-oxblood-500">Activity</p>
        <h1 className="font-display text-3xl text-espresso-700">Your traces</h1>
        <p className="mt-1 text-sm text-warmgray-500">
          Every turn with diagnostics — grounding score, cost, retrieval
          latency, sub-agents that ran. Use the charts to spot anomalies,
          then click into a row to investigate.
        </p>
      </header>

      <ActivityChartStrip
        state={filterState}
        stats={statsQuery.data}
        isLoading={statsQuery.isLoading}
      />

      <ActivityBreakdowns
        state={filterState}
        stats={statsQuery.data}
        isLoading={statsQuery.isLoading}
      />

      <ActivityFiltersBar state={filterState} />

      <section className="rounded border border-ruleline bg-parchment-100">
        {listQuery.isLoading ? (
          <p className="px-4 py-6 text-sm italic text-warmgray-400">Loading activity…</p>
        ) : listQuery.isError ? (
          <p className="px-4 py-6 text-sm italic text-oxblood-500">
            Couldn't load activity:{" "}
            {(listQuery.error as Error)?.message ?? "unknown error"}
          </p>
        ) : traces.length === 0 ? (
          <p className="px-4 py-6 text-sm italic text-warmgray-400">
            No traces match the current filters.
          </p>
        ) : (
          <ul>
            {traces.map((t) => (
              <ActivityRow key={t.trace_id} trace={t} />
            ))}
          </ul>
        )}
      </section>

      <div className="mt-6 flex items-center justify-between">
        <button
          type="button"
          disabled={!canGoBack}
          onClick={() => setCursorStack((s) => s.slice(0, -1))}
          className={cn(
            "border border-ruleline px-4 py-2 text-sm font-serif transition-colors",
            canGoBack
              ? "text-espresso-700 hover:bg-parchment-50 hover:border-oxblood-500"
              : "cursor-not-allowed text-warmgray-300",
          )}
        >
          ← Newer
        </button>

        <button
          type="button"
          disabled={!nextCursor}
          onClick={() => setCursorStack((s) => [...s, nextCursor])}
          className={cn(
            "border border-ruleline px-4 py-2 text-sm font-serif transition-colors",
            nextCursor
              ? "text-espresso-700 hover:bg-parchment-50 hover:border-oxblood-500"
              : "cursor-not-allowed text-warmgray-300",
          )}
        >
          Older →
        </button>
      </div>
    </div>
  );
}
